// src/TextInput.tsx
import { useTheme } from "@emotion/react";
import { FormControl, FormHelperText } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Calendar } from "lucide-react";
import React, { useState } from "react";
import { useApi } from "../../contexts/ApiContext";
import { fontSizes } from "../../theme";
import FormLabel from "../FormLabel";
import { InputProps } from "./InputProps";

export type DateInputPropsType = InputProps;

interface DateInputProps
  extends Omit<Omit<DateInputPropsType, "onChange">, "value"> {
  onChange: (value: Date | null) => void;
  value: Date | null;
  id?: string;
}

const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  error,
  helperText,
  isRequired = true,
  id,
}) => {
  const theme: any = useTheme();
  const [open, setOpen] = useState(false);
  const [cleared, setCleared] = React.useState<boolean>(false);
  const { isSubmitting, isLoading } = useApi();
  const isDisabled = isLoading || isSubmitting;

  console.info("DateInput", { label, value, error, helperText });

  const handleDateChange = (newValue: Date | null) => {
    onChange(newValue);
  };

  const handleClear = () => {
    setCleared(true);
    onChange(null);
  };

  React.useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => {
        setCleared(false);
      }, 1500);

      onChange(null);

      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [cleared]);

  console.info("DateInput error", label, error);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl disabled={isDisabled} error={error} id={id} fullWidth>
        <FormLabel error={error}>{label}</FormLabel>
        <DesktopDatePicker
          disabled={isDisabled}
          sx={{ width: 260 }}
          onOpen={() => setOpen(true)}
          onChange={handleDateChange}
          onClose={() => setOpen(false)}
          open={open}
          value={value ?? null}
          slots={{
            openPickerIcon: Calendar,
          }}
          slotProps={{
            openPickerButton: {
              color: "secondary",
              sx: { color: "rgba(0,0,0,0.5)" },
            },
            textField: {
              sx: { fontSize: fontSizes.sm },
            },
            field: {
              format: "dd/MM/yyyy",
              clearable: true,
              onClear: handleClear,
              sx: { width: "100%" },
            },
          }}
        />
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  );
};

export default DateInput;
